import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";
import { fetchAutenticat } from "../../utils/auth";

const PAGE_SIZES = [10, 25, 50];

function Seleccionartipuspausa({
  onTipusSelect,
  onConsultar,
  onModificar,
  onEsborrar,
  selectedTipus,
  setTipusLlista,
  filterText,
  setFilterText,
  reloadToken,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const [tipus, setTipus] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mostrarActius, setMostrarActius] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "Ordre", direction: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const fetchTipus = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      setLoading(true);
      try {
        const res = await fetchAutenticat(`${API_URL}/TipusPausa.php?idEmpresa=${empresaSeleccionada.IdEmpresa}`);
        const data = await res.json();
        setTipus(data.success ? data.tipus || [] : []);
      } catch (e) {
        console.error("Error carregant Tipus de pausa:", e);
        setTipus([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTipus();
  }, [empresaSeleccionada, reloadToken]);

  useEffect(() => {
    const txt = (filterText || "").toLowerCase();
    let list = tipus.filter((t) => {
      if (mostrarActius && Number(t.Actiu) !== 1) return false;
      if (!mostrarActius && Number(t.Actiu) === 1) return false;
      const camp = [t.Codi, t.Nom].filter(Boolean).join(" ").toLowerCase();
      return camp.includes(txt);
    });

    const { key, direction } = sortConfig;
    if (key) {
      const dir = direction === "asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        const aVal = (a[key] ?? "").toString().toLowerCase();
        const bVal = (b[key] ?? "").toString().toLowerCase();
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      });
    }

    setFiltered(list);
    setTipusLlista?.(list);
    setPage(1);
  }, [filterText, mostrarActius, tipus, sortConfig, setTipusLlista]);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const obrirMenu = (e, item) => {
    e.stopPropagation();
    if (menuObert === item.idTipusPausaEmpleats) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 132;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) top = Math.max(marge, rect.top - alt - 4);
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) left = window.innerWidth - ample - marge;
    setMenuPos({ top, left });
    setMenuObert(item.idTipusPausaEmpleats);
  };

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = filtered.slice(inici, inici + pageSize);
  const itemMenu = menuObert ? filtered.find((t) => t.idTipusPausaEmpleats === menuObert) : null;

  const paginesVisibles = () => {
    const pages = [];
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = paginaActual - 1; i <= paginaActual + 1; i += 1) {
      if (i > 1 && i < totalPages) afegir(i);
    }
    if (totalPages > 1) afegir(totalPages);
    return pages.sort((a, b) => a - b);
  };

  const th = (key, label) => (
    <th className={sortConfig.key === key ? "is-sorted" : ""} onClick={() => handleSort(key)}>
      {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const renderAccions = (item) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === item.idTipusPausaEmpleats ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, item)}
      >
        …
      </button>
    </div>
  );

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per codi o nom…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
        <label className="dv2-filter">
          <span className="dv2-filter-label">Mostra actius</span>
          <span className="dv2-toggle">
            <input type="checkbox" checked={mostrarActius} onChange={() => setMostrarActius((v) => !v)} />
            <span className="dv2-toggle-track" />
          </span>
        </label>
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant tipus de pausa…</p>
      ) : filtered.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap tipus de pausa.</p>
      ) : (
        <>
          {isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {paginaItems.map((t) => (
                <article
                  key={t.idTipusPausaEmpleats}
                  className="dv2-record"
                  onClick={() => onTipusSelect?.(t)}
                  onDoubleClick={() => pucConsultar && onConsultar?.(t)}
                >
                  <div>
                    <p className="dv2-record-title">{t.Nom || "—"}</p>
                    <p className="dv2-record-meta">
                      {t.Codi || "—"} · {t.DuradaPrevistaMinuts ? `${t.DuradaPrevistaMinuts} min` : "Sense durada"}
                    </p>
                  </div>
                  {renderAccions(t)}
                </article>
              ))}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>
                  <tr>
                    {th("Ordre", "Ordre")}
                    {th("Codi", "Codi")}
                    {th("Nom", "Nom")}
                    <th>Computa treball</th>
                    <th>Durada prevista</th>
                    <th>Visible</th>
                    <th>Actiu</th>
                    <th className="dv2-table-actions">Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginaItems.map((t) => (
                    <tr
                      key={t.idTipusPausaEmpleats}
                      className={
                        selectedTipus && Number(selectedTipus.idTipusPausaEmpleats) === Number(t.idTipusPausaEmpleats)
                          ? "is-selected"
                          : ""
                      }
                      onClick={() => onTipusSelect?.(t)}
                      onDoubleClick={() => pucConsultar && onConsultar?.(t)}
                    >
                      <td>{t.Ordre ?? "—"}</td>
                      <td>{t.Codi}</td>
                      <td>{t.Nom}</td>
                      <td>{Number(t.ComputaTempsTreball) === 1 ? "Sí" : "No"}</td>
                      <td>{t.DuradaPrevistaMinuts ? `${t.DuradaPrevistaMinuts} min` : "—"}</td>
                      <td>{Number(t.Visible) === 1 ? "Sí" : "No"}</td>
                      <td>{Number(t.Actiu) === 1 ? "Sí" : "No"}</td>
                      <td className="dv2-table-actions">{renderAccions(t)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage(1)} aria-label="Primera pàgina">
                «
              </button>
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Pàgina anterior">
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button type="button" className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`} onClick={() => setPage(n)}>
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Pàgina següent">
                ›
              </button>
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual >= totalPages} onClick={() => setPage(totalPages)} aria-label="Última pàgina">
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}

      {itemMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onConsultar?.(itemMenu); }}>
                Consultar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onModificar?.(itemMenu); }}>
                Modificar
              </button>
            )}
            {pucEsborrar && (
              <button type="button" className="dv2-menu-item is-danger" onClick={() => { setMenuObert(null); onEsborrar?.(itemMenu); }}>
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default Seleccionartipuspausa;
